// Location service for Indian states and cities
// Using a free public API for Indian location data

export type LocationOption = { value: string; label: string };

export type PincodeAddress = {
  state: string;
  city: string;
  country: string;
};

const POSTAL_PINCODE_API = 'https://api.postalpincode.in';

export function toLocationSlug(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, '-');
}

export function resolveLocationOptionValue(raw: string, options: LocationOption[]): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  const list = Array.isArray(options) ? options : [];
  const byValue = list.find(o => o.value === trimmed);
  if (byValue) {
    return byValue.value;
  }
  const byLabel = list.find(o => String(o.label || '').toLowerCase() === trimmed.toLowerCase());
  if (byLabel) {
    return byLabel.value;
  }
  return toLocationSlug(trimmed);
}

const locationService = {
  // Fetch Indian states
  async getAddressByPinCode(pincode: string): Promise<PincodeAddress | null> {
    const pin = String(pincode || '').replace(/\D/g, '');
    if (pin.length !== 6) {
      return null;
    }

    try {
      const response = await fetch(`${POSTAL_PINCODE_API}/pincode/${pin}`);
      if (!response.ok) {
        throw new Error('Failed to fetch pincode details');
      }

      const data = await response.json();
      const first = Array.isArray(data) ? data[0] : null;
      const postOffice = first?.PostOffice?.[0];

      if (!first || first.Status !== 'Success' || !postOffice) {
        return null;
      }

      return {
        state: String(postOffice.State || '').trim(),
        city: String(postOffice.District || '').trim(),
        country: String(postOffice.Country || 'India').trim() || 'India',
      };
    } catch (error) {
      console.error('Error fetching address by pincode:', error);
      return null;
    }
  },

  async populateAddressFromPincode(
    pincode: string,
    stateOptions: LocationOption[],
    callbacks: {
      setFieldValue: (
        field: string,
        value: string,
        shouldValidate?: boolean
      ) => Promise<void> | void;
      setCityOptions: (options: LocationOption[]) => void;
      setIsLoadingCities: (loading: boolean) => void;
      skipNextCityFetch?: { current: boolean };
      isActive?: () => boolean;
    }
  ): Promise<boolean> {
    const isActive = () => callbacks.isActive?.() !== false;
    try {
      const address = await locationService.getAddressByPinCode(pincode);
      if (!isActive() || !address?.state) {
        return false;
      }

      const stateValue = resolveLocationOptionValue(address.state, stateOptions);
      if (!isActive()) {
        return false;
      }
      if (callbacks.skipNextCityFetch) {
        callbacks.skipNextCityFetch.current = true;
      }

      await callbacks.setFieldValue('state', stateValue, false);
      await callbacks.setFieldValue('country', address.country || 'India', false);

      if (!isActive()) {
        return false;
      }

      callbacks.setIsLoadingCities(true);
      try {
        let cities = await locationService.getCities(stateValue);
        if (!isActive()) {
          return false;
        }
        const district = address.city;
        if (district) {
          const cityValue = resolveLocationOptionValue(district, cities);
          if (!cities.some(c => c.value === cityValue)) {
            cities = [{ value: cityValue, label: district }, ...cities];
          }
          callbacks.setCityOptions(cities);
          await callbacks.setFieldValue('city', cityValue, false);
        } else {
          callbacks.setCityOptions(cities);
        }

        return true;
      } finally {
        if (isActive()) {
          callbacks.setIsLoadingCities(false);
        }
      }
    } catch (error) {
      console.error('Error populating address from pincode:', error);
      if (isActive()) {
        callbacks.setIsLoadingCities(false);
      }
      return false;
    }
  },

  async getStates(): Promise<LocationOption[]> {
    try {
      // Using a free API for Indian states
      // Alternative: You can use https://api.countrystatecity.in/v1/countries/IN/states (requires API key)
      // Or use a static JSON file with Indian states
      const response = await fetch(
        'https://raw.githubusercontent.com/bhanuc/indian-list/master/state-city.json'
      );

      if (!response.ok) {
        throw new Error('Failed to fetch states');
      }

      const data = await response.json();

      // Convert to options format
      const states = Object.keys(data).map(stateName => ({
        value: stateName.toLowerCase().replace(/\s+/g, '-'),
        label: stateName,
      }));

      // Sort alphabetically
      states.sort((a, b) => a.label.localeCompare(b.label));

      return states;
    } catch (error) {
      console.error('Error fetching states:', error);
      // Fallback to common Indian states
      return [
        { value: 'andhra-pradesh', label: 'Andhra Pradesh' },
        { value: 'arunachal-pradesh', label: 'Arunachal Pradesh' },
        { value: 'assam', label: 'Assam' },
        { value: 'bihar', label: 'Bihar' },
        { value: 'chhattisgarh', label: 'Chhattisgarh' },
        { value: 'goa', label: 'Goa' },
        { value: 'gujarat', label: 'Gujarat' },
        { value: 'haryana', label: 'Haryana' },
        { value: 'himachal-pradesh', label: 'Himachal Pradesh' },
        { value: 'jharkhand', label: 'Jharkhand' },
        { value: 'karnataka', label: 'Karnataka' },
        { value: 'kerala', label: 'Kerala' },
        { value: 'madhya-pradesh', label: 'Madhya Pradesh' },
        { value: 'maharashtra', label: 'Maharashtra' },
        { value: 'manipur', label: 'Manipur' },
        { value: 'meghalaya', label: 'Meghalaya' },
        { value: 'mizoram', label: 'Mizoram' },
        { value: 'nagaland', label: 'Nagaland' },
        { value: 'odisha', label: 'Odisha' },
        { value: 'punjab', label: 'Punjab' },
        { value: 'rajasthan', label: 'Rajasthan' },
        { value: 'sikkim', label: 'Sikkim' },
        { value: 'tamil-nadu', label: 'Tamil Nadu' },
        { value: 'telangana', label: 'Telangana' },
        { value: 'tripura', label: 'Tripura' },
        { value: 'uttar-pradesh', label: 'Uttar Pradesh' },
        { value: 'uttarakhand', label: 'Uttarakhand' },
        { value: 'west-bengal', label: 'West Bengal' },
        { value: 'andaman-nicobar', label: 'Andaman and Nicobar Islands' },
        { value: 'chandigarh', label: 'Chandigarh' },
        { value: 'dadra-nagar-haveli', label: 'Dadra and Nagar Haveli' },
        { value: 'daman-diu', label: 'Daman and Diu' },
        { value: 'delhi', label: 'Delhi' },
        { value: 'lakshadweep', label: 'Lakshadweep' },
        { value: 'puducherry', label: 'Puducherry' },
      ];
    }
  },

  // Fetch cities for a given state
  async getCities(stateName: string): Promise<LocationOption[]> {
    try {
      if (!stateName) {
        return [];
      }

      // Convert state value back to proper name for API lookup
      const stateNameFormatted = stateName
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      const response = await fetch(
        'https://raw.githubusercontent.com/bhanuc/indian-list/master/state-city.json'
      );

      if (!response.ok) {
        throw new Error('Failed to fetch cities');
      }

      const data = await response.json();

      // Find the state in the data (handle case-insensitive matching)
      const stateKey = Object.keys(data).find(
        key => key.toLowerCase() === stateNameFormatted.toLowerCase()
      );

      if (!stateKey || !data[stateKey]) {
        return [];
      }

      const cities = data[stateKey];
      if (!Array.isArray(cities)) {
        return [];
      }

      const cityOptions = cities
        .filter((city: unknown): city is string => typeof city === 'string' && city.trim() !== '')
        .map((city: string) => ({
          value: city.toLowerCase().replace(/\s+/g, '-'),
          label: city,
        }));

      // Sort alphabetically
      cityOptions.sort((a, b) => a.label.localeCompare(b.label));

      return cityOptions;
    } catch (error) {
      console.error('Error fetching cities:', error);
      return [];
    }
  },
};

export default locationService;
